<script setup>
import { ref, onMounted, onUnmounted, defineProps, watch } from 'vue';
import * as echarts from "echarts";

import {vacancyRate, effectiveRent} from "@/tool/number";


const props = defineProps({
    data: {
        type: Array,
        default: [
            {name: '2022-Q4', rent: 65.1890625313, vacancyRate: 0.1942065867},
            {name: '2023-Q1', rent: 0, vacancyRate: 0},
            {name: '2023-Q2', rent: 0, vacancyRate: 0},
            {name: '2023-Q3', rent: 0, vacancyRate: 0}
        ]

    },
    color: {
        type: String,
        default: '#54DD88'
    },
    linearGradient:{
        type: Array,
        default: [
            'rgba(84, 221, 136, 0.8)',
            'rgba(255, 255, 255, 0)' 
        ]
    },
    /** 展示的字段 **/
    options: {
        type: Array,
        default: ['name', 'rent', 'vacancyRate']
    },
    labelOptions:{
        type:Array,
        default:['','']
    },
    labelFormat:{
        type:Array,
        default:['','']
    },
    showLabel:{
        type:Array,
        default:['','']
    },
    legends: {
        type: Array,
        default: ['预估有效租金','空置率']
    },
    showLine: {
        type: Number,
        default: true
    },
    notShowYName: {
        type: Boolean,
        default: false
    },
    valueFormat: {
      type: Array
    }
})

const estimateChartDOM = ref(null);
let estimateChartInstance;

function estimateChart() { //长三角城市群高标仓总体量
    let xData = [], y1 = [], y2 = []
    if (props.data.length && props.options.length) {
        const { data, options } = props
        xData = data.map(item => item[options[0]])
        y1 = data.map(item => item[options[1]])
        y2 = data.map(item => item[options[2]])
    }
    estimateChartInstance = echarts.init(estimateChartDOM.value);
    let option = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                lineStyle: {
                    color: props.color
                }
            },
            formatter(a) {
                let year = a[0].axisValue;
                let html = '';
                a.forEach((item, index) => {
                    let pr = props.labelOptions[index] || ''
                    let fn = props.valueFormat[index]
                    let px = props.labelFormat[index] || ''
                    let val = fn ?  fn(item.value) : item.value;

                    let x = '';
                    if (index === 0) {
                        x = `<div class='g-chart-icon g-chart-icon-block' style='background:${props.color}; border-radius: 40%;'></div>`
                    } else if (index === 1) {
                        x = `
                            <div class='g-chart-icon g-chart-icon-line' style='background:#C2DBEC;'>
                                <span style='background:${props.color};'></span>
                                <div class='g-chart-icon-circle' style='border-color:${props.color}'></div>
                            </div>
                        `
                    }
                    html += `
                    <div class='g-chart-item'>
                        ${x}<b>${props.showLabel[index]||''}</b>
                        <span><b>${item.seriesName}：</b>${val + pr + px}</span>
                    </div>
                    `
                })
                return `
                    <div>
                        <p class='g-chart-title' ><b>${year}</b></p>
                        <div class='g-chart-list'>
                            ${html}
                        </div>
                    </div>
                `
            }
        },
        grid: {
            top: 10,
            bottom: 20,
            left: 0,
            right: 20,
            containLabel: true
        },
        xAxis: [
            {
                type: 'category',
                data: xData,
                axisTick: {
                    show: false
                },
                axisLine: {
                    show: false
                },
                axisLabel: {
                    color: '#999'
                }
            }
        ],
        yAxis: [
            {
                name: props.notShowYName ? props.legends[0] : "",

                type: 'value',
                // min: 0,
                // max:function (value) {
                //     return value.max ||10;
                // },
                splitLine: {
                    show: false
                },
                axisLabel: {
                    color:'#666',
                    show: true,
                    formatter: '{value}'+props.labelOptions[0]||'1'
                },

                nameTextStyle: {
                    align: 'left'
                }
            },
            {
                name: props.notShowYName ? props.legends[1] : "",
                axisLabel: {
                    formatter: '{value}'+props.labelOptions[1]||''
                },
                type: 'value',
                splitLine: {
                    show: false
                },
                nameTextStyle: {
                    align: 'right',
                    width: 30
                }
            }
        ],
        series: [
            {
                name: props.notShowYName ? props.legends[0] : "",
                data: y1,
                type: 'bar',
                stack: 'one',
                barWidth: 12,
                itemStyle: {
                    color: props.color,
                    borderRadius: [10, 10, 10, 10]
                },
            },
            {
                name: props.notShowYName ? props.legends[1] : "",
                data: y2,
                type: 'line',
                yAxisIndex: 1,
                symbolSize: 8,
                lineStyle: {
                    color: props.color,
                    width: props.showLine
                },
                itemStyle: {
                    color: props.color
                },
                ...props.showLine ? {
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: props.linearGradient[0]
                            },
                            {
                                offset: 1,
                                color: props.linearGradient[1]
                            }
                        ])
                    }
                }:{}
            }
        ]
    }
    estimateChartInstance.setOption(option);
}

function resizePeiAndLine() {
    estimateChartInstance.resize();
}


watch(props, () => {
    estimateChart();
})

onMounted(() => {
    estimateChart();
    window.addEventListener('resize', resizePeiAndLine);
})

onUnmounted(() => {
    window.removeEventListener('resize', resizePeiAndLine);
})
</script>
<template>
    <div class="u-agg-aii-lbox">
        <div class="u-agg-aii-legend">
            <div class="u-agg-aii-legend-item">
                <b class="u-agg-aii-block" :style="`background-color: ${props.color};`"></b>
                <span>{{props.legends[0] + labelFormat[0]}}</span>
            </div>
            <div class="u-agg-aii-legend-item">
                <div class="u-agg-aii-xx high-standard">
                    <span :style="`background: ${props.color};`" v-show="props.showLine"></span>
                    <div :style="`background-color: #fff; border-color:${props.color}`"></div>
                </div>
                <span>{{props.legends[1] + labelFormat[1]}}</span>
            </div>
        </div>
        <div class="u-agg-chart-total-content" ref="estimateChartDOM"></div>
    </div>
</template>

<style lang="less">
.u-agg-aii-lbox{
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
}

.u-agg-aii-legend {
    display: flex;
    margin-top: 10px;
    margin-bottom: 16px;
    justify-content: space-between;


    .u-agg-aii-legend-item {
        display: flex;
        align-items: center;
        margin-right: 20px;

        .u-agg-aii-block {
            width: 10px;
            height: 10px;
            border-radius: 4px;
            margin-right: 5px;
        }

        .u-agg-aii-xx {
            width: 10px;
            height: 10px;
            margin-right: 8px;
            position: relative;

            div {
                width: 10px;
                height: 10px;
                border-radius: 100%;
                border: 1px solid;
                background: #fff;
                position: absolute;
                left: 0;
                top: 0;

            }

            span {
                position: absolute;
                width: 16px;
                height: 2px;
                top: 50%;
                left: 50%;
                margin-left: -8px;
                margin-top: -1px;
            }


        }

        span {
            color: #666;
        }
    }
}

.u-agg-chart-total-content {
    height: 100%;
}

.g-chart-item {
    display: flex;
    align-items: center;
    font-size: 12px;

    .g-chart-icon {
        margin-right: 10px;

        &.g-chart-icon-block {
            width: 10px;
            height: 10px;
            border-radius: 2px;
        }

        &.g-chart-icon-line {
            width: 10px;
            height: 10px;
            position: relative;
            border-radius: 50%;

            div {
                position: absolute;
                width: 10px;
                height: 10px;
                border-radius: 50%;
                border: 1px solid;
                background: #fff;
            }

            span {
                position: absolute;
                height: 2px;
                top: 50%;
                left: 50%;
                width: 16px;
                margin-left: -8px;
                margin-top: -1px;
            }
        }
    }
}

</style>